import { browserLogger } from "@@/settings";
import type { useFormField } from "@@/shared/form/use-form-field";
import { useUpdateEffect } from "@@/shared/use-update-effect";
import { useTranslate } from "@@/translations/use-translate";
import type {
    Translatable,
    TranslatableString,
    TranslatableValueFactoryParams,
    ZodObjectOf,
    ZodSchemaOf,
    ZodTypeOf} from "@towni/common";
import {
    type TranslatableValue,
    emptyArrayOf,
    generateId,
    translatableStringFactory,
} from "@towni/common";
import { useCallback, useMemo, useRef, useState } from "react";
import { ZodIssueCode } from "zod";
import type { FieldId, FormId } from "./form-and-field-id";
import { useFormFieldIds } from "./form-id.context";
import { useFormStore } from "./form.zustand-store";

type SchemaGetter<State, Value> = (state: Partial<State>) => Value;

type ManualTrigger = () => void;

const validateField = <Value>(
    valueToValidate: Value,
    fieldSchema: ZodObjectOf<Value> | ZodSchemaOf<Value> | ZodTypeOf<Value>,
    customErrorMessage?: Translatable | TranslatableValueFactoryParams,
): TranslatableString[] => {
    const errors: TranslatableString[] = [];
    fieldSchema
        .safeParseAsync(valueToValidate)
        .then(result => {
            if (result.success) {
                return [];
            } else {
                if (customErrorMessage) {
                    errors.push(translatableStringFactory(customErrorMessage));
                    return errors;
                }
                const issues = result.error.issues.map(issue => {
                    if (issue.code === ZodIssueCode.custom) {
                        if (issue.params?.translatedMessage) {
                            return issue.params
                                .translatedMessage as TranslatableString;
                        }
                    }
                    return issue.message;
                });
                for (const issue of issues) {
                    errors.push(translatableStringFactory(issue));
                }
                return errors;
            }
        })
        .catch(err => {
            browserLogger.error(err);
            return [
                translatableStringFactory({
                    sv: "Något gick fel",
                    en: "Something went wrong",
                    de: "Etwas ist schief gelaufen",
                }),
            ];
        });
    return errors;
};

function useFormFieldValidation<
    State extends Record<string, unknown>,
    Value,
>(params: {
    readonly field: ReturnType<typeof useFormField<State, Value>>;
    readonly initialValidationType: "automatic";
    readonly preProcessValue?: (value: Value | undefined) => Value | undefined;
    readonly customFieldValidationErrorMessage?: TranslatableValue;
}): void;
function useFormFieldValidation<
    State extends Record<string, unknown>,
    Value,
>(params: {
    readonly field: ReturnType<typeof useFormField<State, Value>>;
    readonly initialValidationType: "manual";
    readonly preProcessValue?: (value: Value | undefined) => Value | undefined;
    readonly customFieldValidationErrorMessage?: TranslatableValue;
}): ManualTrigger;
function useFormFieldValidation<
    State extends Record<string, unknown>,
    Value,
>(params: {
    readonly field: ReturnType<typeof useFormField<State, Value>>;
    readonly initialValidationType: "automatic" | "manual";
    readonly preProcessValue?: (value: Value | undefined) => Value | undefined;
    readonly customFieldValidationErrorMessage?: TranslatableValue;
}): ManualTrigger | void {
    const field = params.field;
    const initialValidationType = params.initialValidationType ?? "automatic";
    const translate = useTranslate();

    const [triggerId, setTriggerId] = useState({
        type: initialValidationType,
        id: generateId(),
    });
    const validationCountRef = useRef(0);
    const triggerFunc = useCallback((type: "manual" | "automatic") => {
        setTriggerId({ type, id: generateId() });
    }, []);
    useUpdateEffect(
        function autoValidationTrigger() {
            // Automatically trigger a validation when the value changes
            // make sure it's not the initial value when the validation
            // has not been triggered before
            if (
                validationCountRef.current > 0 ||
                (initialValidationType === "automatic" &&
                    field.value !== field.initialValue)
            ) {
                triggerFunc("automatic");
            }
        },
        [field.value],
    );
    const fieldSchema = field.fieldSchema;
    useUpdateEffect(
        function validate() {
            let timeout: NodeJS.Timeout;
            const delay = triggerId.type === "automatic" ? 300 : 0;

            if (fieldSchema?.safeParseAsync) {
                timeout = setTimeout(() => {
                    validationCountRef.current += 1;
                    const valueToValidate = params.preProcessValue
                        ? params.preProcessValue(field.value)
                        : field.value;

                    const errors = validateField(
                        valueToValidate,
                        fieldSchema,
                        params.customFieldValidationErrorMessage,
                    );
                    field.setErrors(
                        errors.map(errorMessage => translate(errorMessage)),
                    );
                }, delay);
            }
            return () => {
                if (timeout) clearTimeout(timeout);
            };
        },
        [triggerId],
    );

    const manualTrigger = useCallback(
        // Trigger a validation, type manual
        () => {
            triggerFunc("manual");
        },
        [triggerFunc],
    );
    return manualTrigger;
}

const useSetAllFieldsToTouched = (formId: FormId): (() => void) => {
    const fieldIdsMapped = useFormFieldIds(formId);
    const fieldIds = useMemo(
        () =>
            fieldIdsMapped
                ? Array.from(fieldIdsMapped.values)
                : emptyArrayOf<FieldId>(),
        [fieldIdsMapped],
    );
    const store = useFormStore();

    const setAllToTouched = useCallback(() => {
        for (const fieldId of fieldIds) {
            store.setTouched(formId)(fieldId)(true);
        }
    }, [fieldIds, formId, store]);
    return setAllToTouched;
};

export { useFormFieldValidation, useSetAllFieldsToTouched };
export type { SchemaGetter };
